import { Stripe } from "stripe";
import { Discounts } from "./discounts";
import { LockerRewardType } from "./locker";
import {
  PowerUpTypes,
  PriceDiscoveryVersion,
  WLRevCatEvent,
  WLSubscriptionsTiers,
} from "./subscriptions";
import { Giveaway } from "./giveaways";
import { Promo } from "./promos";
import {
  WLCreateMetaData,
  WLOmittedMetaData,
  WLUpdateMetaData,
} from "./config";

export interface PostalAddress {
  name: string;
  address: string;
  city: string;
  state: string;
  postCode: string;
  phoneNumber: string; //We can set user's phone number here as a default
}

export type SignInProvider =
  | "google"
  | "fb"
  | "tiktok"
  | "in-app"
  | "apple"
  | "password"
  | "in-app-phone-number";

export const SIGN_IN_PROVIDER_MAP: { [key: string]: SignInProvider } = {
  google: "google",
  fb: "fb",
  tiktok: "tiktok",
  inApp: "in-app",
  apple: "apple",
  password: "password",
  inAppPhoneNumber: "in-app-phone-number",
};

export interface DuplicateRepairEntitlementHold {
  entryPoints: number;
  rewardPoints: number;
  latestMonthlyEntriesViaCron?: {
    points: number;
    updatedDt: number;
  } | null;
  latestMonthlyRewardPointsViaCron?: {
    points: number;
    updatedDt: number;
  } | null;
  latestLockerReward?: {
    type: LockerRewardType;
    reward: number | Discounts;
    currentLockerStreak: number;
    updatedDt: number;
  } | null;
  heldAt: number;
}

export interface LatestFailedUpgradeRevert {
  revertedAt: number;
  subscriptionId: string;
  voidedInvoiceId: string | null;
  previousPriceId: string;
  failedUpgradePriceId: string;
  previousPeriodEnd: number;
  adminNotes?: string;
}

export type LatestDuplicateRepairOutcome =
  | "live-kept"
  | "restored"
  | "hold-cleared";

export interface LatestDuplicateRepair {
  actedAt: number;
  keptSubscriptionId: string;
  cancelledSubscriptionIds: string[];
  outcome: LatestDuplicateRepairOutcome;
  heldEntryPoints: number;
  heldRewardPoints: number;
}

/**
 * for latestLockerViaCron, we'll only use number as type for now, putting in Discounts as a placeholder for phase 2
 */
export interface User {
  // User Basic Data
  docId: string;
  memberId: string;
  firstName: string;
  lastName: string;
  emailAddress: string;
  phoneNumberVerified: boolean;
  phoneNumber: string;
  dateOfBirth?: number; //timestamp new Date().valueOf()
  suburb: string;
  subscription: Omit<
    WLSubscriptionsTiers,
    "stripePriceVersionEntitlements"
  > | null;
  dateSubscribed?: number;
  latestRevCatEvent?: WLRevCatEvent;
  latestStripeEvent?: Stripe.Event;
  latestStripeInvoiceEvent?: Stripe.Event; // for one off or power up purchases
  latestStripeSubscriptionUpdate?: Stripe.Subscription | null; // from pause or unpause or any subscription updates
  /** Audit marker set by Admin Failed Upgrade Revert; does not replace latestStripeEvent */
  latestFailedUpgradeRevert?: LatestFailedUpgradeRevert;
  duplicateRepairEntitlementHold?: DuplicateRepairEntitlementHold | null;
  latestDuplicateRepair?: LatestDuplicateRepair;
  gender: string;
  genderProbability?: number;

  // Rewards related data
  entryPoints: number;
  latestMonthlyEntriesViaCron?: {
    points: number;
    updatedDt: number;
  };
  rewardPoints: number;
  latestMonthlyRewardPointsViaCron?: {
    points: number;
    updatedDt: number;
  };
  latestLockerReward?: {
    type: LockerRewardType;
    reward: number | Discounts;
    currentLockerStreak: number;
    updatedDt: number;
  };
  streakFreezeCount?: number;
  unlockKeyCount?: number;
  latestRefreshDt?: number;

  postalAddress?: PostalAddress;

  klaviyoId?: string;
  gorgiasId?: string;
  stripeId?: string;
  createdDt: number; //timestamp new Date().valueOf()
  updatedDt: number; //timestamp new Date().valueOf()
  photoURL?: string | null;
  isUserWLAdmin?: boolean;
  userAdminRole?: AdminRole;
  adminRoleCode?: string;
  fromInvite?: string;

  signInProvider?: SignInProvider;

  // One off products
  oneOffProducts?: (UserOneOffProducts | UserOneOffBonus)[];

  //source
  source?: string;
  campaign?: string;
  content?: string;
  medium?: string;
  wlLanding?: string;

  //misc
  fcmId?: string;
  platform?: string;
  excluded?: boolean;
  oneOffExpirationDt?: number;
  vipQualified?: boolean;
  legendQualified?: boolean;
  /** Club isolation track. `"club"` after Club join or admin convert. `null` after admin-off. Missing means standard. */
  membershipTrack?: "club" | null;
  isVipRequestToDowngrade?: string | null;
  isDeleted: boolean;
  deletedDt?: number;
  replicatedUserOnly?: boolean; //! means doesnt have a real subscription and was just replicated from production to develop env
  promoCode: string;
  isInfluencer: boolean;
  alternativePriceVersion?: PriceDiscoveryVersion;
  stripeCoupon: string | null;
  joinedOnGiveaway: Giveaway;
  firstSubscription: Omit<
    WLSubscriptionsTiers,
    "stripePriceVersionEntitlements"
  > | null;
  poweredUpOnGiveaway?: Giveaway;

  // cancellation
  isCancelled: boolean;
  cancelReason?: string;
  cancelOtherRemarks?: string;
  cancelDt: number | null;
  discountAppliedForCancelling?: boolean;
  claimedPowerUpForCancelling?: boolean;
  // 14-day cancel-flow extension (internally a pause_collection void, presented as "extended")
  extendedForCancelling?: boolean;
  extensionResumesAt?: number | null;
  extensionLastClaimedAt?: number | null;

  // billing dispute
  hasCurrentBillingDispute?: boolean;
  currentDisputeIds?: string[];

  //notes
  notes?: {
    note: string;
    createdDt: number;
    createdBy?: string;
    createdByEmail?: string;
  }[];

  /** Denormalized count from BigQuery list search; full notes load from Firestore on demand */
  noteCount?: number;

  /** Membership status from BigQuery list search (billingEvents join) */
  membershipStatus?: string;

  /** Purchase/membership channel from BigQuery list search (stripe | ios | android) */
  membershipChannel?: string;

  /** Denormalized giveaway title from BigQuery list search for one-off badge */
  poweredUpOnGiveawayTitle?: string;

  lastAppOpenDt: number | null;
  referredByMemberId: string | null; //! THIS IS ENCRYPTED WHEN SAVED use decryptFromGenericKey from cloud functions,
  installedMobileBuildVersion?: string;

  ipAddress?: string | null;
  userAgent?: string | null;
  attempt_count?: number;
  dontShowAnnouncement?: boolean;
  dontShowWinnersShopIntro?: boolean;
}

export interface UserOneOffProducts {
  invoice: Stripe.Invoice;
  giveAway: Giveaway;
  subscription: WLSubscriptionsTiers;
  entries: number;
  promo?: Promo;
  createdDt: number;
  notes: undefined;
  type: undefined;
  doubleForHalfOffAvailed?: boolean;
}

export interface UserOneOffBonus {
  giveAway: Giveaway;
  entries: number;
  notes: string;
  createdDt: number;
  type?: PowerUpTypes | "bonus";
  invoice: undefined;
  subscription: undefined;
  promo: undefined;
}

export type PowerUpAcquisitionType =
  | 1
  | 2
  | 3
  | 4
  | 5
  | 6
  | 7
  | 8
  | 9
  | 10
  | 11
  | 12
  | 13
  | 14;

export const POWERUP_ACQUISITION_MAP = {
  1: "Cash",
  2: "Locker Win",
  3: "Locker Points",
  4: "Bonus",
  5: "Refer-a-mate bonus",
  6: "Game Rewards",
  7: "Shopify Direct Compensation",
  8: "AWIN Direct Compensation",
  9: "Shop One Off Reward",
  10: "Redeem Code",
  11: "Impact Direct Compensation",
  12: "Commission Factory Direct Compensation",
  13: "Savings Season Rewards",
  14: "Rakuten Direct Compensation",
};

export const PowerUpAcquisitionTypeConfig = {
  cash: { id: 1, name: "Cash" },
  lockerWin: { id: 2, name: "Locker Win" },
  lockerPoints: { id: 3, name: "Locker Points" },
  bonus: { id: 4, name: "Bonus" },
  referral: {
    id: 5,
    name: "Refer-a-mate bonus",
  },
  gameRewards: {
    id: 6,
    name: "Game Rewards",
  },
  shopifyDirectCompensation: {
    id: 7,
    name: "Shopify Direct Compensation",
  },
  awinDirectCompensation: {
    id: 8,
    name: "AWIN Direct Compensation",
  },
  shopOneOffReward: {
    id: 9,
    name: "Shop One Off Reward",
  },
  redeemCode: {
    id: 10,
    name: "Redeem Code",
  },
  impactDirectCompensation: {
    id: 11,
    name: "Impact Direct Compensation",
  },
  commissionFactoryDirectCompensation: {
    id: 12,
    name: "Commission Factory Direct Compensation",
  },
  discountSeasonRewards: {
    id: 13,
    name: "Savings Season Rewards",
  },
  rakutenDirectCompensation: {
    id: 14,
    name: "Rakuten Direct Compensation",
  },
} satisfies {
  [key: string]: {
    id: PowerUpAcquisitionType;
    name: string;
  };
};

export enum SignInAuthProvider {
  password = "password",
  phone = "phone",
}

export interface UserPowerUps extends WLCreateMetaData {
  docId: string;
  userId: string;
  userCurrentSubscription?: null | WLSubscriptionsTiers;
  giveaway: Giveaway;
  type: PowerUpAcquisitionType;
  powerUp: WLSubscriptionsTiers | PowerUpTypes | "bonus";
  invoice: Stripe.Invoice | null;
  invoiceId?: string; // Stripe invoice ID for refund/dispute tracking
  entries: number;
  promo: Promo | null;
  notes: string;
}

export type CreateUserPowerUp = Omit<UserPowerUps, WLOmittedMetaData>;

export interface UserCreate
  extends Omit<
    User,
    | "subscription"
    | "createdDt"
    | "updatedDt"
    | "docId"
    | "entryPoints"
    | "rewardPoints"
    | "latestMonthlyEntriesViaCron"
    | "latestMonthlyRewardPointsViaCron"
    | "latestLockerViaCron"
    | "isUserWLAdmin"
    | "stripeId"
  > {
  password: string;
}

export interface UserWebCreate
  extends Omit<
    User,
    | "subscription"
    | "createdDt"
    | "updatedDt"
    | "docId"
    | "entryPoints"
    | "rewardPoints"
    | "latestMonthlyEntriesViaCron"
    | "latestMonthlyRewardPointsViaCron"
    | "latestLockerViaCron"
    | "isUserWLAdmin"
    | "stripeId"
    | "dateOfBirth"
    | "suburb"
    | "firstSubscription"
    | "joinedOnGiveaway"
    | "stripeCoupon"
  > {
  password: string;
}

export interface UserWebCreate2
  extends Omit<
    User,
    | "subscription"
    | "createdDt"
    | "updatedDt"
    | "docId"
    | "entryPoints"
    | "rewardPoints"
    | "latestMonthlyEntriesViaCron"
    | "latestMonthlyRewardPointsViaCron"
    | "latestLockerViaCron"
    | "isUserWLAdmin"
    | "stripeId"
    | "dateOfBirth"
  > {
  password: string;
}

export interface StripeCustomerExpress {
  emailAddress: string;
  phoneNumber: string;
  referredByMemberId: string | null;
  source?: string;
  campaign?: string;
  content?: string;
  medium?: string;
  wlLanding?: string;
  signInProvider?: SignInProvider;
  alternativePriceVersion?: PriceDiscoveryVersion;
}

export interface UserLogin {
  emailAddress: string;
  password: string;
}

export type SignInCredentials =
  | UserLogin
  | { userId: string; phoneNumber: string; password: string };

export interface UserSignUpStepOne {
  emailAddress: string;
  password: string;
}

export interface ValidateExistingUserLogin {
  emailAddress?: string;
  phoneNumber?: string;
}

export interface WinnerTestament extends WLCreateMetaData, WLUpdateMetaData {
  docId: string;
  photoUrl: string;
  title: string;
  testament: string;
  fullName: string;
  subscription: WLSubscriptionsTiers;
  state: string;
  winningDt: number;
}

export type CreateWinnerTestament = Omit<WinnerTestament, WLOmittedMetaData>;

export interface ExportedUser {
  "Entry #"?: string;
  "Full Name": string;
  Email: string;
  "Phone No.": string;
  Subscription: string;
  Entries?: string;
  Promos?: string;
  Joined: string;
}

export interface ExportedUserPreParsed {
  "Full Name": string;
  Email: string;
  "Phone No.": string;
  Subscription: string;
  Entries?: string;
  Promos?: string;
  Joined: string;
  UTM: string;
}

export interface ExportedUserCondensedPreParsed {
  Entries?: string;
  "Full Name": string;
  Email: string;
  State: string;
}

export interface ExportedUserCondensed {
  "Full Name": string;
  Email: string;
  State: string;
}

export interface GenderResult {
  input: {
    full_name: string; //"Len Gambell";
    country: string; //"AU";
  };
  details: {
    credits_used: number; // 1;
    duration: string; //"45ms";
    samples: number; // 49;
    country: string; //"AU";
    first_name_sanitized: string; //"len";
  };
  result_found: boolean; //true;
  last_name: string; //"Gambell";
  first_name: string; //"Len";
  probability: number; // 0.94;
  gender: string; //"male";
}

export interface GenderResultV2 {
  count: number; //3626;
  name: string; //"kim";
  country_id: string; //"DK";
  gender: string; //"male";
  probability: number; //0.96;
}

export interface UserSession {
  docId: string;
  sessionId: string;
  userId: string;
  createdDt: number;
}

export interface UserSessionCreate extends Omit<UserSession, "docId"> {}

export type AdminRole = 1 | 2 | 3;

export const AdminRoleMap: { [id: number]: string } = {
  1: "Super Admin",
  2: "Admin",
  3: "Support",
};

export interface AdminRoleConfig {
  [key: string]: {
    id: AdminRole;
    name: string;
  };
}

export const ADMIN_ROLE_CONFIG = {
  superAdmin: { id: 1, name: "Super Admin" },
  admin: { id: 2, name: "Admin" },
  support: {
    id: 3,
    name: "Support",
  },
} satisfies AdminRoleConfig;

export interface UserBasicDetails {
  firstName: string;
  lastName: string;
  phoneNumberVerified: boolean;
  phoneNumber: string;
  suburb: string;
  attempt_count?: number;
  dateOfBirth?: number;
}
